import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import useAuth from "@/hooks/useAuth";
import bookingsApi from "@/api/bookingsApi";
import reviewsApi from "@/api/reviewsApi";
import PhotoPlaceholder from "@/components/PhotoPlaceholder/PhotoPlaceholder";
import ReviewCard from "@/components/ReviewCard/ReviewCard";
import ReviewForm from "@/components/ReviewForm/ReviewForm";
import ApiError from "@/model/ApiError";
import BookingResponse from "@/model/BookingResponse";
import ReviewRequest from "@/model/ReviewRequest";
import ReviewResponse from "@/model/ReviewResponse";
import isoDate from "@/utils/isoDate";
import "@/pages/ReviewStayPage/ReviewStayPage.scss";

/** Write, edit or delete the review of one of the guest's stays (route: /trips/:id/review). */
function ReviewStayPage() {
  const { t, i18n } = useTranslation();
  const { session } = useAuth();
  const { id } = useParams<{ id: string }>();

  const [booking, setBooking] = useState<BookingResponse | null>(null);
  const [review, setReview] = useState<ReviewResponse | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);

  const [isEditing, setIsEditing] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<
    "published" | "updated" | "deleted" | null
  >(null);

  useEffect(() => {
    if (!session || !id) {
      return;
    }
    let cancelled = false;
    setIsLoading(true);
    setLoadError(false);

    (async () => {
      try {
        const loadedBooking = await bookingsApi.getBooking(
          session.accessToken,
          id
        );
        const loadedReview = loadedBooking.reviewId
          ? await reviewsApi.getReview(
              loadedBooking.reviewId,
              session.accessToken
            )
          : null;
        if (!cancelled) {
          setBooking(loadedBooking);
          setReview(loadedReview);
        }
      } catch {
        if (!cancelled) {
          setLoadError(true);
        }
      } finally {
        if (!cancelled) {
          setIsLoading(false);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [session, id]);

  if (!session || !id) {
    return null;
  }

  const errorMessage = (err: unknown): string =>
    err instanceof ApiError && (err.status === 400 || err.status === 409)
      ? err.message
      : t("reviews.form.genericError");

  const handleCreate = async (request: ReviewRequest) => {
    setIsSubmitting(true);
    setError(null);
    try {
      setReview(
        await reviewsApi.createReview(session.accessToken, id, request)
      );
      setNotice("published");
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleUpdate = async (request: ReviewRequest) => {
    if (!review?.id) {
      return;
    }
    setIsSubmitting(true);
    setError(null);
    try {
      setReview(
        await reviewsApi.updateReview(session.accessToken, review.id, request)
      );
      setIsEditing(false);
      setNotice("updated");
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async () => {
    if (!review?.id || !window.confirm(t("reviews.page.deleteConfirm"))) {
      return;
    }
    setError(null);
    try {
      await reviewsApi.deleteReview(session.accessToken, review.id);
      setReview(null);
      setNotice("deleted");
      // The stay may be reviewable again if its 14-day window is still open.
      setBooking(await bookingsApi.getBooking(session.accessToken, id));
    } catch (err) {
      setError(errorMessage(err));
    }
  };

  if (isLoading) {
    return (
      <div className="review-stay-page">
        <p>{t("reviews.page.loading")}</p>
      </div>
    );
  }

  if (loadError || !booking) {
    return (
      <div className="review-stay-page">
        <p className="review-stay-page__message" role="alert">
          {t("reviews.page.loadError")}
        </p>
      </div>
    );
  }

  const editable = review?.editableUntil
    ? new Date(review.editableUntil).getTime() > Date.now()
    : false;
  const deadline = booking.reviewDeadline
    ? isoDate.formatIsoDate(booking.reviewDeadline, i18n.language)
    : null;

  const renderBody = () => {
    if (review && isEditing) {
      return (
        <ReviewForm
          initialRating={review.rating}
          initialComment={review.comment}
          submitLabel={t("reviews.page.saveChanges")}
          isSubmitting={isSubmitting}
          error={error}
          onSubmit={handleUpdate}
        />
      );
    }
    if (review) {
      return (
        <>
          <ReviewCard review={review} />
          {editable ? (
            <div className="review-stay-page__actions">
              <button
                type="button"
                className="button-ghost"
                onClick={() => setIsEditing(true)}
              >
                {t("reviews.page.edit")}
              </button>
              <button
                type="button"
                className="review-stay-page__delete"
                onClick={handleDelete}
              >
                {t("reviews.page.delete")}
              </button>
              <span className="review-stay-page__hint">
                {t("reviews.page.editableUntil", {
                  date: new Date(
                    review.editableUntil as string
                  ).toLocaleDateString(i18n.language, {
                    day: "numeric",
                    month: "long",
                  }),
                })}
              </span>
            </div>
          ) : (
            <p className="review-stay-page__hint">{t("reviews.page.locked")}</p>
          )}
          {error && (
            <p className="review-stay-page__error" role="alert">
              {error}
            </p>
          )}
        </>
      );
    }
    if (booking.canReview) {
      return (
        <>
          {deadline && (
            <p className="review-stay-page__hint">
              {t("reviews.page.deadline", { date: deadline })}
            </p>
          )}
          <ReviewForm
            submitLabel={t("reviews.page.publish")}
            isSubmitting={isSubmitting}
            error={error}
            onSubmit={handleCreate}
          />
        </>
      );
    }
    return (
      <p className="review-stay-page__message">
        {t("reviews.page.notReviewable")}
      </p>
    );
  };

  return (
    <div className="review-stay-page">
      <Link to="/trips" className="review-stay-page__back">
        {t("reviews.page.backToTrips")}
      </Link>

      <p className="eyebrow">{t("reviews.page.eyebrow")}</p>
      <h1>{t("reviews.page.title")}</h1>

      <div className="review-stay-page__stay">
        <div className="review-stay-page__thumb">
          {booking.propertyImageUrl ? (
            <img src={booking.propertyImageUrl} alt="" />
          ) : (
            <PhotoPlaceholder />
          )}
        </div>
        <div>
          <strong>{booking.propertyTitle}</strong>
          <span>
            {isoDate.formatIsoDate(booking.checkIn, i18n.language)} →{" "}
            {isoDate.formatIsoDate(booking.checkOut, i18n.language)}
          </span>
        </div>
      </div>

      {notice && (
        <p className="review-stay-page__notice" role="status">
          {t(`reviews.page.notice.${notice}`)}{" "}
          {notice !== "deleted" && booking.propertyId && (
            <Link to={`/properties/${booking.propertyId}`}>
              {t("reviews.page.seeListing")}
            </Link>
          )}
        </p>
      )}

      <div className="review-stay-page__card">{renderBody()}</div>
    </div>
  );
}

export default ReviewStayPage;
